import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import EmptyState from "./EmptyState";
import psiPlatformAdapter, { PlatformNotification } from "../../hooks/psiPlatformAdapter";
import { activityTracker } from "../../utils/activityTracker";
import { useIsPwaApp } from "../../pwa/PwaModeContext";
import { isRouteInPwaApp } from "../../pwa/pwaRoutes";

/**
 * Centro de notificaciones — consume psiplatform-be, que a su vez hace PULL
 * contra psima (fuente de verdad, ver PlatformNotificationsService). psima
 * nunca manda severidad/tipo: todas las notificaciones comparten el mismo
 * ícono. `linkScope`/`linkUrl`/`linkText` solo vienen en notificaciones
 * manuales — las automáticas (PsiChat Flow) siempre los mandan null.
 */

type NotificationTab = "notificaciones" | "pendientes" | "recordatorios";

const TABS: { key: NotificationTab; label: string }[] = [
  { key: "notificaciones", label: "Notificaciones" },
  { key: "pendientes", label: "Pendientes" },
  { key: "recordatorios", label: "Recordatorios" },
];

const UNREAD_POLL_MS = 45_000;

function formatTimeAgo(iso: string): string {
  const sentMs = new Date(iso).getTime();
  if (Number.isNaN(sentMs)) return "";
  const diffMs = Date.now() - sentMs;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "recién";
  if (minutes < 60) return `hace ${minutes} ${minutes === 1 ? "minuto" : "minutos"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} ${hours === 1 ? "hora" : "horas"}`;
  const days = Math.floor(hours / 24);
  return `hace ${days} ${days === 1 ? "día" : "días"}`;
}

/**
 * Navega a linkUrl: interno (empieza con "/") via react-router, externo en pestaña nueva.
 * En la app mobile (`inPwaApp`) la mayoría de las rutas de la web no existen: si el link es interno pero la app mobile
 * no tiene esa pantalla, se abre la plataforma web en el navegador en vez de caer en el inicio sin explicación.
 */
export function openLink(navigate: ReturnType<typeof useNavigate>, url: string, inPwaApp = false) {
  if (/^https?:\/\//i.test(url)) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  // Un link interno se resuelve UNA vez contra el origen del sitio y solo se sigue si sigue siendo de este origen: un
  // `//otro-host/ruta` (que por su ruta podría parecer de la app) o un esquema raro (`javascript:`, `data:`) no se abre ni
  // se navega. Se navega con la ruta ya resuelta, nunca con el texto original.
  let target: URL;
  try {
    target = new URL(url, window.location.origin);
  } catch {
    return;
  }
  if (target.origin !== window.location.origin) return;
  const path = `${target.pathname}${target.search}${target.hash}`;

  if (inPwaApp && !isRouteInPwaApp(path)) {
    window.open(target.href, "_blank", "noopener,noreferrer");
  } else {
    navigate(path);
  }
}

const NotificationMessage = ({
  notification,
  navigate,
  onNavigate,
  inPwaApp,
}: {
  notification: PlatformNotification;
  navigate: ReturnType<typeof useNavigate>;
  onNavigate: () => void;
  inPwaApp: boolean;
}) => {
  const { message, linkUrl, linkScope, linkText } = notification;

  if (linkScope === "INLINE" && linkUrl && linkText && message.includes(linkText)) {
    const idx = message.indexOf(linkText);
    const before = message.slice(0, idx);
    const after = message.slice(idx + linkText.length);
    return (
      <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 4px", whiteSpace: "pre-wrap" }}>
        {before}
        <button
          type="button"
          onClick={() => {
            // Métricas de Actividad: solo el id opaco del aviso; nunca la URL ni el mensaje.
            activityTracker.trackAction("notification_link_clicked", { ref: notification.id });
            onNavigate();
            openLink(navigate, linkUrl, inPwaApp);
          }}
          style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "#185FA5", fontWeight: 600, cursor: "pointer" }}
        >
          {linkText}
        </button>
        {after}
      </p>
    );
  }

  return <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 4px", whiteSpace: "pre-wrap" }}>{message}</p>;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const inPwaApp = useIsPwaApp();
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>("notificaciones");
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<PlatformNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const refreshUnreadCount = useCallback(async () => {
    try {
      const { count } = await psiPlatformAdapter.notificationsUnreadCount();
      setUnreadCount(count);
    } catch {
      // silencioso — se reintenta en el próximo poll, no vale la pena un toast por esto.
    }
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, UNREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { notifications } = await psiPlatformAdapter.notifications();
      setItems(notifications);
    } catch {
      // deja la lista anterior (si había) en vez de vaciarla ante un error transitorio.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Re-consulta la lista mientras el panel está abierto para que las notificaciones
    // nuevas se vean solas — sin esto, la lista quedaba pegada al resultado del último
    // `open` hasta que se cerraba y reabría (o se hacía hard refresh).
    const interval = setInterval(loadNotifications, UNREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [open, loadNotifications]);

  const handleToggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      // Se pide fresco cada vez que se abre — el componente vive montado toda la
      // sesión (no remonta entre navegaciones), así que un "solo la primera vez"
      // dejaría la lista pegada al primer resultado (incluido un "vacío" inicial)
      // para siempre, sin reflejar notificaciones nuevas llegadas después.
      if (next) loadNotifications();
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(items.map((n) => n.id)));
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const markIdsAsRead = async (ids: string[]) => {
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true, readAt: now } : n)));
    setUnreadCount((c) => Math.max(0, c - ids.length));
    try {
      await Promise.all(ids.map((id) => psiPlatformAdapter.markNotificationAsRead(id)));
    } catch {
      // best-effort: si falla, el próximo poll de unread-count corrige el numerito.
    }
  };

  const markSelectedAsRead = () => {
    const ids = Array.from(selectedIds);
    setSelectedIds(new Set());
    markIdsAsRead(ids);
  };

  const handleCardClick = (item: PlatformNotification) => {
    if (!item.read) markIdsAsRead([item.id]);
    if (item.linkScope === "WHOLE" && item.linkUrl) {
      activityTracker.trackAction("notification_link_clicked", { ref: item.id });
      setOpen(false);
      openLink(navigate, item.linkUrl, inPwaApp);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="Notificaciones"
        onClick={handleToggleOpen}
        style={{
          position: "relative",
          width: 34,
          height: 34,
          borderRadius: 9999,
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <Bell size={17} color="#fff" />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              background: "#ef4444",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              minWidth: 16,
              height: 16,
              borderRadius: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 3px",
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed sm:absolute top-[calc(4rem+env(safe-area-inset-top,0px))] sm:top-11 left-3 right-3 sm:left-auto sm:right-0 w-auto sm:w-[360px]"
          style={{
            background: "#fff",
            border: "1.5px solid #1f2937",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            zIndex: 3000,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 4,
              background: "#f3f4f6",
              borderBottom: "1.5px solid #1f2937",
              padding: "8px 8px 0",
            }}
          >
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className="px-2 text-[11.5px] sm:px-2.5 sm:text-[12.5px]"
                style={{
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                  background: activeTab === tab.key ? "#fff" : "none",
                  border: "1.5px solid",
                  borderColor: activeTab === tab.key ? "#1f2937" : "transparent",
                  borderBottom: "none",
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  paddingTop: 9,
                  paddingBottom: 9,
                  fontWeight: 600,
                  color: activeTab === tab.key ? "#2d2049" : "#6b7280",
                  boxShadow: activeTab === tab.key ? "0 -3px 0 #6D57BC inset" : "none",
                  cursor: "pointer",
                }}
              >
                {tab.label}
                {tab.key === "notificaciones" && unreadCount > 0 && (
                  <span
                    style={{
                      background: "#fee2e2",
                      color: "#b91c1c",
                      borderRadius: 9999,
                      fontSize: 11,
                      padding: "1px 6px",
                      marginLeft: 4,
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {activeTab !== "notificaciones" ? (
            <div style={{ padding: "1.5rem 1rem" }}>
              <EmptyState
                title={activeTab === "pendientes" ? "Sin pendientes" : "Sin recordatorios"}
                description="Todavía no hay nada acá."
                noBorder
              />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 14px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b7280", cursor: "pointer" }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ width: 14, height: 14 }} disabled={items.length === 0} />
                  Seleccionar todas
                </label>
                <button
                  type="button"
                  disabled={selectedIds.size === 0}
                  onClick={markSelectedAsRead}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 12,
                    color: selectedIds.size === 0 ? "#9ca3af" : "#185FA5",
                    cursor: selectedIds.size === 0 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Check size={14} /> Marcar leídas
                </button>
              </div>

              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {loading ? (
                  <div style={{ padding: "1.5rem 1rem", textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
                    Cargando notificaciones…
                  </div>
                ) : items.length === 0 ? (
                  <div style={{ padding: "1.5rem 1rem" }}>
                    <EmptyState title="Sin notificaciones" description="Estás al día." noBorder />
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <div
                      key={item.id}
                      onClick={() => handleCardClick(item)}
                      style={{
                        display: "flex",
                        gap: 10,
                        padding: "12px 14px",
                        borderBottom: idx < items.length - 1 ? "1px solid #f3f4f6" : "none",
                        background: item.read ? "#fafafa" : "#fff",
                        cursor: "pointer",
                        boxShadow: item.linkScope === "WHOLE" ? "inset 0 0 0 1px #dbe6f5" : "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelectOne(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginTop: 3, width: 14, height: 14, flexShrink: 0 }}
                      />
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: "#e6f1fb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Bell size={15} color="#185FA5" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "#111827" }}>{item.title ?? "Notificación"}</p>
                          {!item.read && (
                            <span
                              style={{
                                width: 11,
                                height: 11,
                                borderRadius: 9999,
                                background: "#ef4444",
                                boxShadow: "0 0 0 3px rgba(239,68,68,0.2)",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </div>
                        <NotificationMessage notification={item} navigate={navigate} onNavigate={() => setOpen(false)} inPwaApp={inPwaApp} />
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <p style={{ fontSize: 11, color: "#9ca3af", margin: 0 }}>{formatTimeAgo(item.sentAt)}</p>
                        </div>
                        {item.linkScope === "WHOLE" && (
                          <p style={{ fontSize: 11, color: "#185FA5", fontWeight: 600, margin: "4px 0 0" }}>
                            {item.linkText ?? "Toda la notificación es clickeable"} →
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
